import { useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import {
    AGENT_BEACON_PACKET_CAPTURE,
    AGENT_BEACON_PACKETS_DISABLED_OPTION,
    AGENT_BEACON_PACKETS_ENABLED_OPTION,
    AGENT_BEACON_PACKETS_FIELD,
    AGENT_BEACON_PACKETS_GLOBAL_OPTION,
    AGENT_BEACON_PACKETS_INTERFACES_GLOBAL_OPTION,
    AGENT_BEACON_PACKETS_SELECTION_FIELD,
    AGENT_BEACON_PACKETS_WIRED_SELECTION_FIELD,
    AGENT_BEACON_PACKETS_WIRELESS_SELECTION_FIELD,
    AGENT_BEACON_VIRTUAL_INTERFACES_WIRED_ENABLED_FIELD,
    AGENT_BEACON_VIRTUAL_INTERFACES_WIRED_NUMBER_FIELD,
    AGENT_BEACON_VIRTUAL_INTERFACES_WIRED_OVERRIDDEN_FIELD,
    AGENT_BEACON_VIRTUAL_INTERFACES_WIRELESS_ENABLED_FIELD,
    AGENT_BEACON_VIRTUAL_INTERFACES_WIRELESS_NUMBER_FIELD,
    AGENT_BEACON_VIRTUAL_INTERFACES_WIRELESS_OVERRIDDEN_FIELD,
    AGENT_FIRMWARE_VERSION_FIELD,
    AGENT_IS_DYNAMIC_WIRELESS_ENABLED_FIELD,
    AGENT_IS_WIRELESS_ENABLED_FIELD,
    AGENT_LATEST_AGENT_VERSION,
    AGENT_RECOMMENDED_UPDATE,
    AGENT_REFRESH_INTERVAL,
    AGENT_REQUIRES_UPDATE,
    AGENT_SOFTWARE_VERSION_FIELD,
    AGENT_SSID_FIELD,
    AGENT_NETWORK_SSID_FIELD,
    AGENT_STATUS_FIELD,
    AGENT_STATUS_RUNNING,
    AGENT_WEB_PROXY_ADDRESS,
    AGENT_WEB_PROXY_AUTO_PROXY_OPTION,
    AGENT_WEB_PROXY_AUTOCONFIG_URL,
    AGENT_WEB_PROXY_DEFAULT_PROXY_OPTION,
    AGENT_WEB_PROXY_ENCRYPTED_PASSWORD,
    AGENT_WEB_PROXY_ENCRYPTED_USERNAME,
    AGENT_WEB_PROXY_MANUAL_PROXY_OPTION,
    AGENT_WEB_PROXY_OVERRIDDEN,
    AGENT_WEB_PROXY_PORT,
    AGENT_WEB_PROXY_SELECTION_FIELD,
    AGENT_WEB_PROXY_USE_DEFAULT,
    AGENT_WIFI_DEFAULT_OPTION,
    AGENT_WIFI_DISABLED_OPTION,
    AGENT_WIFI_MULTI_SSID_OPTION,
    AGENT_WIFI_SELECTION_FIELD,
    AGENT_WIFI_SINGLE_SSID_OPTION,
    GLOBAL_AGENT_SETTINGS_FIELD,
    AGENT_WIRED_DEFAULT_OPTION,
    AGENT_WIRED_FIELD,
    AGENT_CERTIFICATE_NAME_FIELD,
    AGENT_CERTIFICATE_DESCRIPTION_FIELD,
    AGENT_WIFI_STATIC_IP_ENABLED,
    AGENT_WIFI_PERMACADDR_ENABLED,
} from './AgentConstants';
import {
    AGENT_BEACON_PACKETS_INTERFACES_DISABLED_OPTION,
    AGENT_VIRTUAL_INTERFACE_WIRED_COUNT_FIELD,
    AGENT_VIRTUAL_INTERFACE_WIRED_FIELD,
    AGENT_VIRTUAL_INTERFACE_WIRELESS_COUNT_FIELD,
    AGENT_VIRTUAL_INTERFACE_WIRELESS_FIELD,
    BEACON_PACKETS_FIELD,
} from '../agent-settings/AgentSettingsConstants';
import { useGenericError } from '../../../services/FormErrorService';
import { HTTP_STATUS_400, useDataService } from '../../../services/ApiDataService';
import { buildVoIPRegistration } from '../agents-voip/AgentVoIPHelper';
import { AGENT_SOFTWARE_VERSION } from '../general/Constants';
import { formatLatestAgentVersion, getAgentVersion, isSoftwareOutOfDate } from './AgentVersionHelper';
import useUiAlerts, { createAlertConfig, ERROR } from '../../../stores/AlertsStore';
import { errorAlertObject } from '../../form/FormConstants';

import {
    isHardware,
    isHardware3k,
    hasWireless,
    isWindows,
    canCollectLogs,
    isGlobal,
    isHardwareEdgeSensor,
} from './AgentHelper';
import format, { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';

export function buildAgentWebProxy(dbValue) {
    if (_.get(dbValue, AGENT_WEB_PROXY_ADDRESS)) {
        _.set(dbValue, AGENT_WEB_PROXY_SELECTION_FIELD, AGENT_WEB_PROXY_MANUAL_PROXY_OPTION);
        return;
    }

    if (_.get(dbValue, AGENT_WEB_PROXY_AUTOCONFIG_URL)) {
        _.set(dbValue, AGENT_WEB_PROXY_SELECTION_FIELD, AGENT_WEB_PROXY_AUTO_PROXY_OPTION);
        return;
    }

    _.set(dbValue, AGENT_WEB_PROXY_SELECTION_FIELD, AGENT_WEB_PROXY_DEFAULT_PROXY_OPTION);
}

export function prepareWebProxyFields(putData, formData) {
    // If they've overridden the proxy there is no logic
    if (_.get(formData, AGENT_WEB_PROXY_OVERRIDDEN, false)) {
        return;
    }

    // If we are here reset the overridden flag
    _.set(putData, AGENT_WEB_PROXY_OVERRIDDEN, false);

    const proxySelection = _.get(putData, AGENT_WEB_PROXY_SELECTION_FIELD);
    if (proxySelection !== AGENT_WEB_PROXY_AUTO_PROXY_OPTION) {
        _.set(putData, AGENT_WEB_PROXY_AUTOCONFIG_URL, null);
    }

    if (proxySelection !== AGENT_WEB_PROXY_MANUAL_PROXY_OPTION) {
        _.set(putData, AGENT_WEB_PROXY_ADDRESS, null);
        _.set(putData, AGENT_WEB_PROXY_PORT, null);
    }

    _.set(putData, AGENT_WEB_PROXY_USE_DEFAULT, proxySelection === AGENT_WEB_PROXY_DEFAULT_PROXY_OPTION);

    _.set(
        putData,
        AGENT_WEB_PROXY_ENCRYPTED_USERNAME,
        saveWebProxyEncryptedObject(
            _.get(putData, AGENT_WEB_PROXY_ENCRYPTED_USERNAME),
            _.get(putData, AGENT_WEB_PROXY_USE_DEFAULT)
        )
    );

    _.set(
        putData,
        AGENT_WEB_PROXY_ENCRYPTED_PASSWORD,
        saveWebProxyEncryptedObject(
            _.get(putData, AGENT_WEB_PROXY_ENCRYPTED_PASSWORD),
            _.get(putData, AGENT_WEB_PROXY_USE_DEFAULT)
        )
    );
}

function saveWebProxyEncryptedObject(encryptedObject, isUsingDefault) {
    if (isUsingDefault || !encryptedObject) {
        return null;
    }

    if (_.isEmpty(_.get(encryptedObject, 'value', ''))) {
        return null;
    }

    return encryptedObject;
}

function getGlobalBeaconSetting(globalSettings) {
    return _.get(globalSettings, BEACON_PACKETS_FIELD, false) ? i18n._(t`Enabled`) : i18n._(t`Disabled`);
}

function getGlobalBeaconInterfacesSetting(globalSettings, enabledField, countField) {
    if (!_.get(globalSettings, enabledField, false)) {
        return 'No';
    }

    return _.get(globalSettings, countField, 1);
}

export function buildBeaconSettings(dbGlobalAgentSettings, dbAgent) {
    const agentBeaconSetting = _.get(dbAgent, AGENT_BEACON_PACKETS_FIELD, null);

    // Boolean value, if null it means the global setting is used.
    if (agentBeaconSetting === null) {
        _.set(dbAgent, AGENT_BEACON_PACKETS_SELECTION_FIELD, AGENT_BEACON_PACKETS_GLOBAL_OPTION);
    } else {
        _.set(
            dbAgent,
            AGENT_BEACON_PACKETS_SELECTION_FIELD,
            agentBeaconSetting ? AGENT_BEACON_PACKETS_ENABLED_OPTION : AGENT_BEACON_PACKETS_DISABLED_OPTION
        );
    }

    // WIRED INTERFACES - Moving the db values into a drop down
    _.set(
        dbAgent,
        AGENT_BEACON_PACKETS_WIRED_SELECTION_FIELD,
        getInterfaceSelection(
            dbAgent,
            AGENT_BEACON_VIRTUAL_INTERFACES_WIRED_OVERRIDDEN_FIELD,
            AGENT_BEACON_VIRTUAL_INTERFACES_WIRED_ENABLED_FIELD,
            AGENT_BEACON_VIRTUAL_INTERFACES_WIRED_NUMBER_FIELD
        )
    );

    // WIRELESS INTERFACES - Moving the db values into a drop down
    _.set(
        dbAgent,
        AGENT_BEACON_PACKETS_WIRELESS_SELECTION_FIELD,
        getInterfaceSelection(
            dbAgent,
            AGENT_BEACON_VIRTUAL_INTERFACES_WIRELESS_OVERRIDDEN_FIELD,
            AGENT_BEACON_VIRTUAL_INTERFACES_WIRELESS_ENABLED_FIELD,
            AGENT_BEACON_VIRTUAL_INTERFACES_WIRELESS_NUMBER_FIELD
        )
    );

    return _.extend(buildGlobalBeaconSettings(dbGlobalAgentSettings));
}

function getInterfaceSelection(dbAgent, overriddenField, enabledField, numberField) {
    if (!_.get(dbAgent, overriddenField)) {
        return AGENT_BEACON_PACKETS_INTERFACES_GLOBAL_OPTION;
    }

    if (!_.get(dbAgent, enabledField)) {
        return AGENT_BEACON_PACKETS_INTERFACES_DISABLED_OPTION;
    }

    return _.get(dbAgent, numberField);
}

function buildGlobalBeaconSettings(dbGlobalAgentSettings) {
    const globalSelection = getGlobalBeaconSetting(dbGlobalAgentSettings);

    const globalWiredSelection = getGlobalBeaconInterfacesSetting(
        dbGlobalAgentSettings,
        AGENT_VIRTUAL_INTERFACE_WIRED_FIELD,
        AGENT_VIRTUAL_INTERFACE_WIRED_COUNT_FIELD
    );
    const globalWirelessSelection = getGlobalBeaconInterfacesSetting(
        dbGlobalAgentSettings,
        AGENT_VIRTUAL_INTERFACE_WIRELESS_FIELD,
        AGENT_VIRTUAL_INTERFACE_WIRELESS_COUNT_FIELD
    );

    const globalOptionText = i18n._(t`Use global value from nPoint Settings (${globalSelection})`);
    const globalWiredOptionText = i18n._(t`Use global value from nPoint Settings (${globalWiredSelection} interfaces)`);
    const globalWirelessOptionText = i18n._(
        t`Use global value from nPoint Settings (${globalWirelessSelection} interfaces)`
    );

    return {
        [GLOBAL_AGENT_SETTINGS_FIELD]: {
            ...dbGlobalAgentSettings,
            globalWiredOptionText,
            globalWirelessOptionText,
            globalOptionText,
        },
    };
}

export function buildWiFi(dbAgent) {
    _.set(dbAgent, AGENT_WIFI_SELECTION_FIELD, getWiFiSelection(dbAgent));

    if (_.isEmpty(_.get(dbAgent, AGENT_SSID_FIELD))) {
        _.set(dbAgent, AGENT_SSID_FIELD, AGENT_WIFI_DEFAULT_OPTION);
    }
    if (_.isUndefined(_.get(dbAgent, AGENT_WIFI_PERMACADDR_ENABLED))) {
        _.set(dbAgent, AGENT_WIFI_PERMACADDR_ENABLED, false);
    }
    if (_.isUndefined(_.get(dbAgent, AGENT_WIFI_STATIC_IP_ENABLED))) {
        _.set(dbAgent, AGENT_WIFI_STATIC_IP_ENABLED, false);
    }
}

function getWiFiSelection(dbAgent) {
    if (_.get(dbAgent, AGENT_IS_WIRELESS_ENABLED_FIELD, false)) {
        return AGENT_WIFI_SINGLE_SSID_OPTION;
    }

    if (_.get(dbAgent, AGENT_IS_DYNAMIC_WIRELESS_ENABLED_FIELD, false)) {
        return AGENT_WIFI_MULTI_SSID_OPTION;
    }

    return AGENT_WIFI_DISABLED_OPTION;
}

export function prepareWiFiFields(putData, formData) {
    _.set(
        putData,
        AGENT_IS_WIRELESS_ENABLED_FIELD,
        _.get(putData, AGENT_WIFI_SELECTION_FIELD) === AGENT_WIFI_SINGLE_SSID_OPTION
    );
    _.set(
        putData,
        AGENT_IS_DYNAMIC_WIRELESS_ENABLED_FIELD,
        _.get(putData, AGENT_WIFI_SELECTION_FIELD) === AGENT_WIFI_MULTI_SSID_OPTION
    );

    if (_.get(putData, AGENT_SSID_FIELD) === AGENT_WIFI_DEFAULT_OPTION) {
        _.set(putData, AGENT_SSID_FIELD, '');
    }

    // set a second instance of the ssid that is never changed to 'default' and is always valid if used in ssidConfig api url
    _.set(putData, AGENT_NETWORK_SSID_FIELD, _.get(putData, AGENT_SSID_FIELD));

    // If they are using multi-mode, save off their old selected single mode wi-fi network
    //  otherwise we send up null and which saves off the default
    if (_.get(putData, AGENT_WIFI_SELECTION_FIELD) === AGENT_WIFI_MULTI_SSID_OPTION) {
        _.set(putData, AGENT_SSID_FIELD, _.get(formData, AGENT_SSID_FIELD));
    }

    _.set(
        putData,
        AGENT_WIFI_PERMACADDR_ENABLED,
        (_.get(putData, AGENT_WIFI_SELECTION_FIELD) === AGENT_WIFI_SINGLE_SSID_OPTION ||
            _.get(putData, AGENT_WIFI_SELECTION_FIELD) === AGENT_WIFI_MULTI_SSID_OPTION) &&
            _.get(putData, AGENT_WIFI_PERMACADDR_ENABLED)
    );
}

export function prepareBeaconFields(putData) {
    const beaconSelection = _.get(putData, AGENT_BEACON_PACKETS_SELECTION_FIELD);

    _.set(putData, AGENT_BEACON_PACKETS_FIELD, getBeaconPacketsValue(putData));

    if (beaconSelection === AGENT_BEACON_PACKETS_DISABLED_OPTION) {
        _.set(putData, AGENT_BEACON_PACKET_CAPTURE, false);
    }

    prepareBeaconInterfaces(putData, beaconSelection);
}

function getBeaconPacketsValue(putData) {
    const beaconSelection = _.get(putData, AGENT_BEACON_PACKETS_SELECTION_FIELD);

    if (beaconSelection === AGENT_BEACON_PACKETS_GLOBAL_OPTION) {
        return null;
    }

    return beaconSelection === AGENT_BEACON_PACKETS_ENABLED_OPTION;
}

function prepareBeaconInterfaces(putData) {
    const wiredSelection = _.get(putData, AGENT_BEACON_PACKETS_WIRED_SELECTION_FIELD);
    const wirelessSelection = _.get(putData, AGENT_BEACON_PACKETS_WIRELESS_SELECTION_FIELD);

    // WIRED
    if (!_.isUndefined(wiredSelection)) {
        _.set(
            putData,
            AGENT_BEACON_VIRTUAL_INTERFACES_WIRED_OVERRIDDEN_FIELD,
            wiredSelection !== AGENT_BEACON_PACKETS_INTERFACES_GLOBAL_OPTION
        );
        _.set(
            putData,
            AGENT_BEACON_VIRTUAL_INTERFACES_WIRED_ENABLED_FIELD,
            wiredSelection !== AGENT_BEACON_PACKETS_INTERFACES_DISABLED_OPTION
        );
        _.set(putData, AGENT_BEACON_VIRTUAL_INTERFACES_WIRED_NUMBER_FIELD, getInterfaceNumberValue(wiredSelection));
    }

    if (!_.isUndefined(wirelessSelection)) {
        // WIRELESS
        _.set(
            putData,
            AGENT_BEACON_VIRTUAL_INTERFACES_WIRELESS_OVERRIDDEN_FIELD,
            wirelessSelection !== AGENT_BEACON_PACKETS_INTERFACES_GLOBAL_OPTION
        );
        _.set(
            putData,
            AGENT_BEACON_VIRTUAL_INTERFACES_WIRELESS_ENABLED_FIELD,
            wirelessSelection !== AGENT_BEACON_PACKETS_INTERFACES_DISABLED_OPTION
        );
        _.set(
            putData,
            AGENT_BEACON_VIRTUAL_INTERFACES_WIRELESS_NUMBER_FIELD,
            getInterfaceNumberValue(wirelessSelection)
        );
    }
}

function getInterfaceNumberValue(selection) {
    const defaultNumberToByPassValidations = 1;
    if (
        selection !== AGENT_BEACON_PACKETS_INTERFACES_GLOBAL_OPTION &&
        selection !== AGENT_BEACON_PACKETS_INTERFACES_DISABLED_OPTION
    ) {
        return parseInt(selection, 10);
    }

    return defaultNumberToByPassValidations;
}

function buildSelectedWiFiOption(agent, network) {
    return `${network.nameFmt} `;
}

export function buildWiFiNetworkOptions(configuredNetworks, agent) {
    const defaultNetworkOption = {
        _id: AGENT_WIFI_DEFAULT_OPTION,
        nameFmt: '(Default Wi-Fi Network Profile)',
    };

    let hasDefaultNetwork = false;

    const builtNetworks = _.chain(configuredNetworks)
        .sortBy((network) => _.toLower(network.profileName))
        .map((network) => {
            network.nameFmt = `${network.profileName}`;

            if (network.isDefault) {
                // Slightly different so we can let them know this is the default network
                //  the format will look like "ngp-test-open (Default Wi-Fi Network)"
                hasDefaultNetwork = true;
                if (_.isEmpty(agent.ssid) || AGENT_WIFI_DEFAULT_OPTION === agent.ssid) {
                    _.set(
                        defaultNetworkOption,
                        'nameFmt',
                        `${buildSelectedWiFiOption(agent, network)}${_.get(defaultNetworkOption, 'nameFmt')}`
                    );
                    _.extend(defaultNetworkOption, _.omit(network, ['_id', 'nameFmt']));
                    return network;
                }

                _.set(defaultNetworkOption, 'nameFmt', `${network.nameFmt} ${_.get(defaultNetworkOption, 'nameFmt')}`);
                _.extend(defaultNetworkOption, _.omit(network, ['nameFmt']));
                return network;
            }

            // See if this is the matching id.
            if (network._id === agent.ssid) {
                network.nameFmt = `${buildSelectedWiFiOption(agent, network)}`;
                return network;
            }

            return network;
        })
        .filter((network) => !network.isDefault)
        .value();

    // Put the default option in front
    if (!hasDefaultNetwork) {
        defaultNetworkOption.nameFmt = 'Default Wi-Fi Network Profile (not set)';
    }
    builtNetworks.unshift(defaultNetworkOption);

    return builtNetworks;
}

export function buildWired(dbAgent) {
    if (_.isEmpty(_.get(dbAgent, AGENT_WIRED_FIELD))) {
        _.set(dbAgent, AGENT_WIRED_FIELD, AGENT_WIRED_DEFAULT_OPTION);
    }
}

export function prepareWiredFields(putData) {
    if (_.get(putData, AGENT_WIRED_FIELD) === AGENT_WIRED_DEFAULT_OPTION) {
        _.set(putData, AGENT_WIRED_FIELD, '');
    }
}

function buildSelectedWiredOption(agent, network) {
    return `${network.nameFmt} `;
}

export function buildWiredNetworkOptions(configuredNetworks, agent) {
    const defaultNetworkOption = {
        _id: AGENT_WIRED_DEFAULT_OPTION,
        nameFmt: '(Default Wired Network)',
    };

    let hasDefaultNetwork = false;

    const builtNetworks = _.chain(configuredNetworks)
        .sortBy((network) => _.toLower(network.profileName))
        .map((network) => {
            network.nameFmt = `${network.profileName}`;

            if (network.isDefault) {
                // Slightly different so we can let them know this is the default network
                //  the format will look like "ngp-test-open (Default Wired Network)"
                hasDefaultNetwork = true;
                if (_.isEmpty(agent.wired) || AGENT_WIRED_DEFAULT_OPTION === agent.wired) {
                    _.set(
                        defaultNetworkOption,
                        'nameFmt',
                        `${buildSelectedWiredOption(agent, network)}${_.get(defaultNetworkOption, 'nameFmt')}`
                    );
                    _.extend(defaultNetworkOption, _.omit(network, ['_id', 'nameFmt']));
                    return network;
                }

                _.set(defaultNetworkOption, 'nameFmt', `${network.nameFmt} ${_.get(defaultNetworkOption, 'nameFmt')}`);
                _.extend(defaultNetworkOption, _.omit(network, ['nameFmt']));
                return network;
            }

            // See if this is the matching id.
            if (network._id === agent.wired) {
                network.nameFmt = `${buildSelectedWiredOption(agent, network)}`;
                return network;
            }

            return network;
        })
        .filter((network) => !network.isDefault)
        .value();

    // Put the default option in front
    if (!hasDefaultNetwork) {
        defaultNetworkOption.nameFmt = 'Default Wired Network (not set)';
    }
    builtNetworks.unshift(defaultNetworkOption);

    return builtNetworks;
}

function getFirmwareVersion(version) {
    const versionSeparator = '_';
    if (version && version.indexOf(versionSeparator) < 0) {
        return null;
    }

    const [, firmwareVersion] = version.split(versionSeparator);
    return firmwareVersion;
}

export function buildVersion(agent, pulseSoftwareVersion) {
    const agentVersion = getAgentVersion(agent);
    const latestVersion = formatLatestAgentVersion(_.get(pulseSoftwareVersion, AGENT_SOFTWARE_VERSION.VERSION_STRING));
    const breakingVersion =
        _.get(pulseSoftwareVersion, AGENT_SOFTWARE_VERSION.BREAKING_VERSION) ||
        `${_.get(pulseSoftwareVersion, AGENT_SOFTWARE_VERSION.BREAKING_MAJOR_VERSION)}.${_.get(
            pulseSoftwareVersion,
            AGENT_SOFTWARE_VERSION.BREAKING_MINOR_VERSION
        )}`;

    _.set(agent, AGENT_LATEST_AGENT_VERSION, latestVersion);
    _.set(agent, AGENT_REQUIRES_UPDATE, isSoftwareOutOfDate(agentVersion, breakingVersion));
    _.set(agent, AGENT_RECOMMENDED_UPDATE, isSoftwareOutOfDate(agentVersion, latestVersion));
    _.set(agent, AGENT_FIRMWARE_VERSION_FIELD, getFirmwareVersion(_.get(agent, AGENT_SOFTWARE_VERSION_FIELD)));
}

function postLatestAgentDataHandler(response, pulseSoftwareVersion) {
    buildVoIPRegistration(response);
    buildVersion(response, pulseSoftwareVersion);

    return _.extend({}, response);
}

export function useLatestAgentData(agentId) {
    const agentQueryUrl = `/query/admin/agents/${agentId}`;
    const dataService = useDataService();
    const [latestAgentData, setLatestAgentData] = useState(null);
    const isWindowsAgent = useRef(false);
    const isHardware3kAgent = useRef(false);
    const isHardwareEdgeSensorAgent = useRef(false);
    const isWirelessAgent = useRef(false);
    const isHardwareAgent = useRef(false);
    const allowCollectLogs = useRef(true);
    const isGlobalAgent = useRef(false);

    const handleGenericError = useGenericError();
    const intervalHandle = useRef(null);

    const refreshLatestAgentData = async () => {
        if (intervalHandle.current) {
            clearInterval(intervalHandle.current);
        }

        const {
            environment: { pulseSoftwareVersion },
        } = await dataService.get('/ipm/v1/config/environment');

        const findFunc = _.partial(findLatestAgentData, pulseSoftwareVersion);
        intervalHandle.current = setInterval(findFunc, AGENT_REFRESH_INTERVAL);

        return findFunc();
    };

    const findLatestAgentData = async (pulseSoftwareVersion) => {
        try {
            const queryAgent = await dataService.get(agentQueryUrl);

            const agentData = postLatestAgentDataHandler(queryAgent, pulseSoftwareVersion);

            isWindowsAgent.current = isWindows(agentData);
            isHardware3kAgent.current = isHardware3k(agentData);
            isHardwareAgent.current = isHardware(agentData);
            isWirelessAgent.current = hasWireless(agentData);
            isHardwareEdgeSensorAgent.current = isHardwareEdgeSensor(agentData);
            allowCollectLogs.current = canCollectLogs(agentData);
            isGlobalAgent.current = isGlobal(agentData);

            setLatestAgentData(agentData);
        } catch (e) {
            handleGenericError(e);
        }
    };

    useEffect(() => {
        refreshLatestAgentData();
        return () => clearInterval(intervalHandle.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        latestAgentData,
        refreshLatestAgentData,
        isWindowsAgent,
        isHardware3kAgent,
        isHardwareAgent,
        isWirelessAgent,
        isHardwareEdgeSensorAgent,
        allowCollectLogs,
        isGlobalAgent,
    };
}

function collectDeleteError(errorDetails) {
    const testNeedsPulseKey = 'test_needs_pulse';

    if (errorDetails && errorDetails.message === testNeedsPulseKey) {
        const pulseName = _.get(errorDetails, 'pulseName');
        const testNames = _.get(errorDetails, 'testNames');
        return i18n._(
            t`The nPoint ${pulseName} could not be deleted because it is required for the following Service Test(s): ${testNames}`
        );
    }

    return i18n._(
        t`There was a problem communicating with the server.  If this issue persists, please contact support.`
    );
}

export function useDeleteErrorHandler() {
    const [, uiAlertActions] = useUiAlerts();

    return useCallback(
        (errorObject) => {
            let alertConfig = errorAlertObject;
            if (errorObject.getStatus && errorObject.getStatus() === HTTP_STATUS_400) {
                const responseText = _.get(errorObject, 'responseBody');
                const alertText = _.isString(responseText) ? responseText : collectDeleteError(responseText.details);
                alertConfig = createAlertConfig(ERROR, alertText);
            }

            // We may have handled errors at this point, but leaving here in case we want
            // to separate error handing.
            uiAlertActions.createAlert(alertConfig);

            // eslint-disable-next-line no-console
            console.error('useDeleteErrorHandler Error', errorObject.responseBody || errorObject);
        },
        [uiAlertActions]
    );
}

export function durationDisplay(time, agent) {
    if (!time || _.get(agent, AGENT_STATUS_FIELD) !== AGENT_STATUS_RUNNING) {
        return null;
    }

    return `About ${moment.duration(time * 1000).humanize()} (${time}s)`;
}

export function roundVital(float, digits, suffix) {
    if (!float || float < 0) {
        return null;
    }
    return `${parseFloat(float).toFixed(digits)}${suffix}`;
}

export function formatDatetime(time) {
    const certDate = Date.parse(time);
    return certDate >= 0 ? format(certDate, { type: FORMATTER_TYPES.DATETIME }) : null;
}

export function buildCertDataSource(certificates, certUsage) {
    return {
        url: `/query/admin/nPointCertificate`,
        totalProperty: 'count',
        dataProperty: 'data',
        params: {
            query: JSON.stringify({
                _id: { $in: certificates },
                certificateUsage: certUsage,
            }),
        },
    };
}

export function buildCertificateColumns() {
    return [
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Name`,
            sortable: true,
            mapping: AGENT_CERTIFICATE_NAME_FIELD,
            width: 135,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Description`,
            sortable: true,
            mapping: AGENT_CERTIFICATE_DESCRIPTION_FIELD,
            width: 300,
        },
    ];
}
